import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UzButton, UzInput, UzSwitch } from '@utazzo/ui';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import React, { useState } from 'react';

import {
  navHrefLockHint,
  navItemTypeLabel,
  isNavHrefLocked,
  type FlatDraftItem,
} from './navigation-tree.utils';

interface Props {
  item: FlatDraftItem;
  canWrite: boolean;
  expanded: boolean;
  bulkMode: boolean;
  bulkSelected: boolean;
  onToggleExpand: () => void;
  onToggleBulk: () => void;
  onChange: (patch: Partial<FlatDraftItem>) => void;
  onRemove: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  canIndent: boolean;
  canOutdent: boolean;
}

/** WordPress-style menu item row: collapsed bar + expandable settings panel. */
export const WpMenuItemCard = ({
  item,
  canWrite,
  expanded,
  bulkMode,
  bulkSelected,
  onToggleExpand,
  onToggleBulk,
  onChange,
  onRemove,
  onIndent,
  onOutdent,
  canIndent,
  canOutdent,
}: Props): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !canWrite,
  });

  const [label, setLabel] = useState(item.label);
  const [href, setHref] = useState(item.href ?? '');
  const hrefLocked = isNavHrefLocked(item);
  const lockHint = navHrefLockHint[item.itemType];

  const subLabel =
    item.depth > 0 ? `sub item${item.depth > 1 ? ` (level ${item.depth + 1})` : ''}` : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        marginLeft: item.depth * 28,
      }}
      className={`overflow-hidden rounded border border-(--border) bg-(--bg) ${
        isDragging ? 'z-10 opacity-70 shadow-lg' : ''
      } ${item.isDeleted ? 'hidden' : ''} ${!item.isVisible ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-1 bg-(--z-50) px-2 py-2">
        {bulkMode && (
          <input
            type="checkbox"
            checked={bulkSelected}
            onChange={onToggleBulk}
            aria-label={`Select ${item.label}`}
          />
        )}
        <button
          type="button"
          className="cursor-grab text-(--fg-faint) hover:text-(--fg) active:cursor-grabbing disabled:opacity-30"
          disabled={!canWrite}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onToggleExpand}
          className="text-(--fg-faint) hover:text-(--fg)"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-(--fg)">
          {item.label}
          {subLabel && (
            <span className="ml-2 text-xs font-normal text-(--fg-faint)">{subLabel}</span>
          )}
        </span>
        <span className="shrink-0 text-xs text-(--fg-faint)">
          {navItemTypeLabel[item.itemType]}
        </span>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-(--border) p-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-(--fg-faint)">
              Navigation Label
            </label>
            <UzInput
              value={label}
              disabled={!canWrite}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => label !== item.label && onChange({ label })}
              className="h-8"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-(--fg-faint)">
              {hrefLocked ? 'URL (read-only)' : 'URL'}
            </label>
            <UzInput
              value={href}
              disabled={!canWrite || hrefLocked}
              readOnly={hrefLocked}
              onChange={(e) => setHref(e.target.value)}
              onBlur={() =>
                !hrefLocked && (href || null) !== item.href && onChange({ href: href || null })
              }
              className="h-8 font-mono text-xs"
              placeholder="/path or https://…"
            />
            {hrefLocked && lockHint && (
              <p className="mt-1 text-[11px] text-(--fg-faint)">{lockHint}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-(--fg)">
              <input
                type="checkbox"
                checked={item.openInNewTab}
                disabled={!canWrite}
                onChange={(e) => onChange({ openInNewTab: e.target.checked })}
              />
              Open link in a new tab
            </label>
            <label className="flex items-center gap-2 text-xs text-(--fg)">
              <UzSwitch
                checked={item.isVisible}
                disabled={!canWrite}
                onCheckedChange={(v) => onChange({ isVisible: v })}
                aria-label="Visible on site"
              />
              Visible
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <UzButton
              type="button"
              size="sm"
              variant="outline"
              disabled={!canWrite || !canOutdent}
              onClick={onOutdent}
            >
              Outdent
            </UzButton>
            <UzButton
              type="button"
              size="sm"
              variant="outline"
              disabled={!canWrite || !canIndent}
              onClick={onIndent}
            >
              Indent
            </UzButton>
            <UzButton
              type="button"
              size="sm"
              variant="outline"
              className="text-(--ds-error) hover:text-(--ds-error)"
              disabled={!canWrite}
              onClick={onRemove}
            >
              Remove
            </UzButton>
          </div>
        </div>
      )}
    </div>
  );
};
